import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { TextCutter, Tooltip } from '@anytag/shared-atoms';
import { cn, formatIntNumber, formatPercent } from '@anytag/shared-utils';
import { EngagementPostTrafficSource, YoutubeTrafficSource } from '@anytag/graphql';
import { HighChart, Highcharts } from '../../../high-chart';

export interface TrafficSourceChartProps {
  className?: string;
  data: readonly EngagementPostTrafficSource[]; // Only support Engagement for now
}
export const TrafficSourceChart = ({ data, className }: TrafficSourceChartProps) => {
  const { t } = useTranslation();

  const getContent = (key: YoutubeTrafficSource) => {
    switch (key) {
      case YoutubeTrafficSource.ADVERTISING:
        return {
          name: t('Source.Advertising'),
          help: t('Tooltip.The viewer was referred to the video by an advertisement')
        };
      case YoutubeTrafficSource.ANNOTATION:
        return {
          name: t('Source.Annotation'),
          help: t('Tooltip.Viewers reached the video by clicking on an annotation in another video')
        };
      case YoutubeTrafficSource.CAMPAIGN_CARD:
        return {
          name: t('Source.Campaign card'),
          help: t(
            'Tooltip.Views originated from claimed, user-uploaded videos that the content owner used to promote the viewed content'
          )
        };
      case YoutubeTrafficSource.END_SCREEN:
        return {
          name: t('Source.End screen'),
          help: t('Tooltip.The views were referred from the end screen of another video')
        };
      case YoutubeTrafficSource.EXT_URL:
        return {
          name: t('Source.Ext URL'),
          help: t('Tooltip.The video views were referred from a link on another website')
        };
      case YoutubeTrafficSource.HASHTAGS:
        return {
          name: t('Source.Hashtags'),
          help: t('Tooltip.Views originated from VOD hashtag pages or Shorts hashtag pivot pages')
        };
      case YoutubeTrafficSource.LIVE_REDIRECT:
        return {
          name: t('Source.Live redirect'),
          help: t('Tooltip.The video views were referred from Live Redirects')
        };
      case YoutubeTrafficSource.NO_LINK_EMBEDDED:
        return {
          name: t('Source.No link embedded'),
          help: t('Tooltip.The video was embedded on another website when it was viewed')
        };
      case YoutubeTrafficSource.NO_LINK_OTHER:
        return {
          name: t('Source.No link other'),
          help: t(
            'Tooltip.YouTube did not identify a referrer for the traffic This category encompasses direct traffic to a video as well as traffic on mobile apps'
          )
        };
      case YoutubeTrafficSource.NOTIFICATION:
        return {
          name: t('Source.Notification'),
          help: t('Tooltip.The video views were referred from an email or notification from YouTube')
        };
      case YoutubeTrafficSource.PLAYLIST:
        return {
          name: t('Source.Playlist'),
          help: t('Tooltip.The video views occurred while the video was being played as part of a playlist')
        };
      case YoutubeTrafficSource.PRODUCT_PAGE:
        return {
          name: t('Source.Product page'),
          help: t('Tooltip.The video views were referred from a Product page')
        };
      case YoutubeTrafficSource.PROMOTED:
        return {
          name: t('Source.Promoted'),
          help: t(
            'Tooltip.The video views were referred from an unpaid YouTube promotion, such as the YouTube "Spotlight Videos" page'
          )
        };
      case YoutubeTrafficSource.RELATED_VIDEO:
        return {
          name: t('Source.Related video'),
          help: t('Tooltip.The video views were referred from a related video listing on another video watch page')
        };
      case YoutubeTrafficSource.SHORTS:
        return {
          name: t('Source.Youtube shorts'),
          help: t(
            'Tooltip.The viewer was referred by swiping vertically from the previous video in the Shorts viewing experience'
          )
        };
      case YoutubeTrafficSource.SOUND_PAGE:
        return {
          name: t('Source.Sound page'),
          help: t('Tooltip.Views originated from Shorts sound pivot pages')
        };
      case YoutubeTrafficSource.SUBSCRIBER:
        return {
          name: t('Source.Subscriber'),
          help: t(
            'Tooltip.The video views were referred from feeds on the YouTube homepage or from YouTube subscription features'
          )
        };
      case YoutubeTrafficSource.YT_CHANNEL:
        return {
          name: t('Source.Youtube channel'),
          help: t('Tooltip.The video views occurred on a channel page')
        };
      case YoutubeTrafficSource.YT_OTHER_PAGE:
        return {
          name: t('Source.Youtube other pages'),
          help: t(
            'Tooltip.The video views were referred from a link other than a search result or related video link that appeared on a YouTube page'
          )
        };
      case YoutubeTrafficSource.YT_PLAYLIST_PAGE:
        return {
          name: t('Source.Youtube playlist page'),
          help: t('Tooltip.The video views originated from a page that lists all of the videos in a playlist')
        };
      case YoutubeTrafficSource.YT_SEARCH:
        return {
          name: t('Source.Youtube search'),
          help: t('Tooltip.The video views were referred from YouTube search results')
        };
      case YoutubeTrafficSource.VIDEO_REMIXES:
        return {
          name: t('Source.Video remixes'),
          help: t('Tooltip.The video views were referred from the remixed video link in the Shorts player')
        };

      default:
        return {
          name: '',
          help: 'Tooltip.'
        };
    }
  };

  const totalViews = data?.reduce((sum, curr) => curr.views + sum, 0);
  const formattedSourceData =
    totalViews && data
      ? data.map((item, i) => ({
          name: item.sourceType,
          y: parseFloat((item.views / totalViews).toFixed(4)),
          value: item.views,
          color: styles.colors[i]
        }))
      : [];
  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      height: 400,
      width: 400,
      borderRadius: 0,
      backgroundColor: '#F6F8FA'
    },
    title: {
      text: ''
    },
    legend: {
      enabled: false
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        innerSize: 120,
        dataLabels: {
          enabled: false
        },
        showInLegend: false
      }
    },
    series: [
      {
        type: 'pie',
        colors: styles.colors,
        data: formattedSourceData
      }
    ],
    tooltip: {
      formatter(): string {
        return renderToStaticMarkup(
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className={styles.sourceIndicator} style={{ backgroundColor: this.color as string }} />
              <span className="text-base text-white">{getContent(this.name as YoutubeTrafficSource).name}</span>
            </div>
            <span className="text-base text-white">{formatPercent(this.y)}</span>
          </div>
        );
      },
      shadow: false,
      padding: 8,
      borderWidth: 0,
      borderRadius: 8,
      backgroundColor: '#27313B',
      style: { color: '#fff' },
      useHTML: true
    }
  };

  return (
    <div className={cn('flex gap-4', className)}>
      <div className="flex-none">
        <HighChart options={options} />
      </div>
      <div className="flex-1">
        <table
          className={cn(
            'w-full min-w-90 border-collapse',
            '[&_td]:w-1/2 [&_td]:border-base-border [&_td]:p-2.5',
            '[&_th]:w-1/2 [&_th]:border-base-border [&_th]:p-2.5'
          )}
        >
          <thead>
            <tr className="font-semibold">
              <th>{t('Traffic Source')}</th>
              <th>{t('HeaderColumn.Video view')}</th>
            </tr>
          </thead>
          <tbody>
            {formattedSourceData.map((source) => {
              const selectedContent = getContent(source.name);

              return (
                <tr key={source.name}>
                  <td>
                    <div className="flex items-center">
                      <span
                        className={cn(styles.sourceIndicator, 'mr-2')}
                        style={{ backgroundColor: source?.color as string }}
                      />
                      <Tooltip help={selectedContent.help}>
                        <span className="cursor-pointer hover:underline">{selectedContent.name}</span>
                      </Tooltip>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-baseline">
                      <TextCutter text={formatIntNumber(source.value)} lines={1} />
                      &nbsp;
                      <span className="text-[10px] text-tertiary">{formatPercent(source.y)}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  sourceIndicator: cn('mr-1 inline-block size-1 shrink-0 rounded-full'),
  colors: [
    '#A32530',
    '#4992C3',
    '#FDCDBE',
    '#F43E08',
    '#F89499',
    '#68A5CD',
    '#E28018',
    '#F9DEC2',
    '#79AFD3',
    '#D85262',
    '#EC9E4A',
    '#FDC8B7',
    '#8CBAD9',
    '#F5676E',
    '#FCCACD',
    '#F2C08A',
    '#B3D1E6',
    '#FEDDDF',
    '#F5CDA2',
    '#FCAD94',
    '#F24049',
    '#589BC8'
  ]
};
